import { Title } from '@mantine/core'
import { Site } from '@pubstack/common/src/site'
import { FunctionComponent, ReactNode } from 'react'
import { Alert } from '~/components/Alert'
import Button from '~/components/Button'
import { CodeSnippet } from '~/components/CodeSnippet'
import { FoldingContent } from '~/components/FoldingContent'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Link } from '~/components/Link'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { useScope } from '~/hooks/useScope'
import { ConnectNote, ConnectStep, connectPlanOf, connectSnippetOf } from './connectPlan'

const STEP_TITLE: Record<ConnectStep, string> = {
  'html-tag': 'HTML - script tag',
  'javascript-tag': 'Javascript - inline code',
  gtm: 'Google tag manager',
  'pubstack-adapter': 'Pubstack adapter',
  'sdk-init': 'Prebid SDK initialisation',
  'amp-code': 'AMP code',
  'amp-cookie-sync': 'Cookie sync',
}

const stepDescription = (trackingPrefix: string): Record<ConnectStep, ReactNode> => ({
  'html-tag': (
    <p>
      Add the following script into the <code>&lt;head&gt;</code> section of every page. For better performance, load it before any other.
    </p>
  ),
  'javascript-tag': 'Copy the following code into your javascript file.',
  gtm: (
    <p>
      Add Pubstack through your <Link label={'Google Tag Manager'} href={'https://tagmanager.google.com/'} trackingLabel={`${trackingPrefix}-tag-manager-navigate` as TrackingLabel} />. Its{' '}
      <Link
        label={'documentation'}
        href={'https://support.google.com/tagmanager/answer/6107167?hl=en&ref_topic=3002579'}
        trackingLabel={`${trackingPrefix}-tag-manager-documentation-navigate` as TrackingLabel}
      />{' '}
      walks through the steps.
    </p>
  ),
  'pubstack-adapter': 'Add the following to your Prebid configuration to connect this media to Pubstack through the Pubstack adapter.',
  'sdk-init': 'Add the following code to your app to connect it to Pubstack.',
  'amp-code': 'Add the following code to your pages to link AMP to this media.',
  'amp-cookie-sync': 'Add the following code for cookie sync.',
})

const NOTE_CONTENT: Record<ConnectNote, ReactNode> = {
  'pubstack-adapter': (
    <>
      Your Prebid.js build must include the{' '}
      <Link label={'Pubstack bid adapter'} href={'https://docs.prebid.org/dev-docs/bidders/pubstack.html'} trackingLabel={'common-connect-pbs-adapter-navigate'} variant="secondary" target="_blank" />{' '}
      (<code>pubstackBidAdapter</code>, Prebid.js 11.4 or later).
      <br />
      Don’t forget to add Pubstack as vendor <code>1408</code> in your CMP.
    </>
  ),
  'prebid-sdk': (
    <>
      Don’t forget to set up the{' '}
      <Link label={'Prebid Module SDK'} href={'https://docs.prebid.org/prebid-mobile/prebid-mobile.html'} trackingLabel={'common-connect-sdk-adapter-navigate'} variant="secondary" target="_blank" />{' '}
      in your app before connecting it to Pubstack.
    </>
  ),
  // One replaces whatever was monetising the page, so this is a warning rather than a tip.
  'site-preparation': (
    <>
      Remove every existing monetisation from the pages before connecting — GAM, Amazon, Prebid. Remove Pubstack’s own analytics tag too, if the media already carries one: this tag includes both the
      analytics and the ad management features.
    </>
  ),
}

const PBJS_TIP = 'If a Prebid variable other than "pbjs" is used, please inform your Customer Success Manager or the Pubstack support team: helpdesk@pubstack.io.'

type Props = {
  /** The media to connect. The creation modal passes a copy carrying a placeholder id, for a batch. */
  site: Site
  /** Prefix for every tracking label inside, so each surface stays distinguishable in the analytics. */
  trackingPrefix: string
  /**
   * Whether each step can be collapsed, and so starts collapsed.
   *
   * A flyout and the creation modal exist to show this and nothing else, so they lay every step open. A
   * page tab sits among others and folds them away.
   */
  isFolding?: boolean
  /**
   * Where "Go to activation" leads. Without it there is no activation panel at all: a surface with nowhere
   * to send the reader would be telling them to enable a product and giving them no way to do it.
   */
  onActivate?: () => void
  /** Ask for the plain Analytics tag whatever the media runs on — see `ConnectAudience`. */
  analyticsOnly?: boolean
}

/**
 * The instructions for connecting a media to Pubstack — the one copy of them.
 *
 * There used to be five: the media flyout, the Node and One media pages, the settings flyout and the modal
 * that opens the moment a media is created. They drifted, and not only in wording — three of them wrote the
 * boot URL out by hand, so a One media connected from any of those three was handed the analytics-only tag
 * and never booted its ad management at all.
 *
 * What to show is decided in `connectPlan`, from the media's product and environment and the scope's
 * features; this renders that answer. What one surface owns alone — Node's server-side ad unit table, One's
 * link to the analytics-only tag — stays outside it, beside this component.
 */
export const ConnectToPubstack: FunctionComponent<Props> = ({ site, trackingPrefix, isFolding = true, onActivate, analyticsOnly }) => {
  const toast = useToast()
  const scope = useScope()
  const plan = connectPlanOf(site, { hasAnalytics: scope.hasFeature('analytics'), analyticsOnly })
  const descriptions = stepDescription(trackingPrefix)

  const onCodeCopy = (label: string) =>
    toast.success(
      <>
        <strong>{label}</strong> was successfully copied to your clipboard.
      </>
    )

  // Shown beside the steps rather than instead of them: a media sitting on Node's pages with Node switched
  // off still has an Analytics tag worth pasting, and hiding it would make the page emptier than it is.
  const activation = plan.pendingActivation && onActivate && (
    <IllustrationMessage iconName="night_sky" iconSize="190px" title="Enable Prebid server-side monetisation to generate the script." message="">
      <Button variant="primary" onClick={onActivate} trackingLabel={`${trackingPrefix}-activation-navigate` as TrackingLabel}>
        Go to activation
      </Button>
    </IllustrationMessage>
  )

  if (plan.steps.length === 0) {
    return activation || <p className="m-0 font-roboto text-base leading-[1.625rem] text-hurricane">This media has nothing to connect.</p>
  }

  return (
    <div className="flex flex-col gap-4">
      {plan.lead && <p className="m-0 font-roboto text-base leading-[1.625rem] text-jet">{plan.lead}</p>}

      {plan.notes.map((note) => (
        <Alert key={note} variant="secondary" iconName="tips">
          <div>{NOTE_CONTENT[note]}</div>
        </Alert>
      ))}

      {/* Not on One, which serves its own Prebid build under `kleanadsPbjs` and locks the variable: there is no
          page variable for the reader to report. `analyticsOnly` lands on `analytics`, where there is one again. */}
      {plan.env === 'web' && plan.product !== 'adm' && (
        <Tooltip title={PBJS_TIP}>
          <span className="font-roboto text-sm leading-[1.375rem] italic text-hurricane">Your Prebid variable is not pbjs?</span>
        </Tooltip>
      )}

      {/* `[&_code]:w-full` undoes `CodeSnippet`'s own max-width, which leaves the multi-line snippets
          wrapping in a column a third of the panel wide. Four call sites each carried their own copy. */}
      <div className="flex flex-col [&_code]:w-full">
        {plan.steps.map((step) => {
          const snippet = connectSnippetOf(step, site, plan.product)
          return (
            <FoldingContent key={step} title={<Title order={5}>{STEP_TITLE[step]}</Title>} isFolding={isFolding}>
              <div className="flex flex-col font-roboto text-base leading-[1.625rem] text-jet">
                {descriptions[step]}
                {snippet && <CodeSnippet trackingLabel={`${trackingPrefix}-${step}-copy` as TrackingLabel} onCodeCopy={() => onCodeCopy(STEP_TITLE[step])} contentText={snippet} />}
              </div>
            </FoldingContent>
          )
        })}
      </div>

      {activation}
    </div>
  )
}
